import * as THREE from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { FileInfo } from './file-info'
import { cloneGlbHierarchyUserDataMeta, collectGlbHierarchy, extractPartName } from './glb'
import { applyOriginalNodeNamesToScene, resolveImportedDisplayName } from './original-node-name'
import {
  applyGltfBodyEntriesToScene,
  cloneWithSharedGeometry,
  hasLocalTransform,
  resolveReferencedPartHierarchyRoot,
} from './part'
import { applyGltfBrepBindingPlansToScene } from './imported-brep-binding-plan'
import { resolveImportedModelUnitContextFromGltf } from './imported-model-unit-context'
import { createImportedAssemblyLocalTopologyPayload } from './create-imported-assembly-local-topology-payload'
import { inheritStrippedRootUserData } from './stripped-root-user-data'
import { activateImportedModelTopologyHosts } from '../topology/imported-model-topology-runtime'
import { ImportedModelObjectMetadataService } from '../metadata'
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent'
import type { GlbHierarchyNode, GlbHierarchyNodeMeta, ImportedBodyEntry } from './glb'
import {
  AssemblyComponentFixationService,
  AssemblyComponentFixationState,
} from '../../assembly/component-fixation'
import { IMPORTED_MODEL_RUNTIME_CACHE_REVISION } from './runtime-cache-revision'
import { asRecord } from './utils'
import { normalizeImportedHierarchyDuplicateName } from './normalize-imported-hierarchy-duplicate-name'
import type {
  AssemblyManifest,
  AssemblyModelAsset,
  AssemblyManifestLoadResult,
  AssemblyNode,
  ComposedAssemblyResult,
  PartAsset,
  SceneBindingIndex,
  SelectionIndex,
} from './types'

import {
  cloneInlineAssemblyLocalGeometry,
  cloneMetadata,
  hasInlineAssemblyLocalGeometry,
  isGeometryObject,
  resolveBodyHighlightTargetsById,
  resolveCanonicalAssemblyRoot,
} from './assembly-node-utils'
import {
  shouldSkipResolvedExternalReferenceFallback,
} from './assembly-external-reference-fallback'
import { analyzeGltfAssemblyScene } from './assembly-analysis/analyzeGltfAssemblyScene'

export function parseAssemblyManifestGltf(
  gltf: GLTF,
  file: FileInfo,
  version: string,
): AssemblyManifestLoadResult {
  applyOriginalNodeNamesToScene(gltf)
  applyGltfBrepBindingPlansToScene(gltf)
  applyGltfBodyEntriesToScene(gltf)
  const { canonicalRoot, strippedWrappers } = resolveCanonicalAssemblyRoot(gltf.scene)
  inheritStrippedRootUserData(canonicalRoot, strippedWrappers)
  gltf.scene.updateMatrixWorld(true)
  const analysis = analyzeGltfAssemblyScene(gltf, file, version)

  return {
    manifest: analysis.manifest,
    sourceGltf: gltf,
    warnings: [...analysis.warnings],
    inlinePartSourceByNodeId: Object.fromEntries(analysis.inlinePartSourceByNodeId),
    inlineAssemblySourceByNodeId: Object.fromEntries(analysis.inlineAssemblySourceByNodeId),
  }
}

function buildAssemblyAssetKey(asset: AssemblyModelAsset): string {
  return `${asset.fileUuid}:${asset.version}`
}

export function createReferencedAssemblyModelAsset(
  manifest: AssemblyManifest,
  partAssetsByUuid: Record<string, PartAsset>,
  assemblyAssetsByUuid: Record<string, AssemblyModelAsset>,
  file: FileInfo,
  version: string,
  warnings: string[] = [],
  sourceScene?: THREE.Object3D,
): AssemblyModelAsset {
  const localGeometryTemplate = (() => {
    if (!sourceScene) {
      return null
    }

    const { canonicalRoot, strippedWrappers } = resolveCanonicalAssemblyRoot(sourceScene)
    inheritStrippedRootUserData(canonicalRoot, strippedWrappers)
    if (!hasInlineAssemblyLocalGeometry(canonicalRoot)) {
      return null
    }

    return cloneInlineAssemblyLocalGeometry(canonicalRoot)
  })()
  const topologyPayload = localGeometryTemplate && hasInlineAssemblyLocalGeometry(localGeometryTemplate)
    ? createImportedAssemblyLocalTopologyPayload(localGeometryTemplate, manifest.unitContext)
    : null

  return {
    fileUuid: file.uuid,
    fileId: file.fileId,
    version,
    cacheRevision: IMPORTED_MODEL_RUNTIME_CACHE_REVISION,
    partName: extractPartName(file.fileId),
    sourceType: 'referenced',
    manifest,
    partAssetsByUuid,
    assemblyAssetsByUuid,
    bodyEntries: topologyPayload?.bodyEntries,
    warnings: [...warnings],
    sceneTemplate: topologyPayload?.sceneTemplate,
    localTopologySource: topologyPayload?.localTopologySource,
  }
}

export function createEmbeddedAssemblyModelAsset(
  sceneTemplate: THREE.Object3D,
  manifest: AssemblyManifest,
  file: FileInfo,
  version: string,
  warnings: string[] = [],
  bodyEntries: ImportedBodyEntry[] = [],
): AssemblyModelAsset {
  const { canonicalRoot, strippedWrappers } = resolveCanonicalAssemblyRoot(sceneTemplate)
  inheritStrippedRootUserData(canonicalRoot, strippedWrappers)
  const topologyPayload = createImportedAssemblyLocalTopologyPayload(
    cloneWithSharedGeometry(canonicalRoot),
    manifest.unitContext,
  )
  return {
    fileUuid: file.uuid,
    fileId: file.fileId,
    version,
    cacheRevision: IMPORTED_MODEL_RUNTIME_CACHE_REVISION,
    partName: extractPartName(file.fileId),
    sourceType: 'embedded',
    manifest,
    partAssetsByUuid: {},
    assemblyAssetsByUuid: {},
    bodyEntries: topologyPayload.bodyEntries ?? (bodyEntries.length > 0 ? bodyEntries : undefined),
    warnings: [...warnings],
    sceneTemplate: topologyPayload.sceneTemplate,
    localTopologySource: topologyPayload.localTopologySource,
  }
}

export function createInlineAssemblyModelAsset(
  source: THREE.Object3D,
  nodeId: string,
  nodeName: string,
  assemblyFileUuid: string,
  version: string,
  unitContext?: AssemblyManifest['unitContext'],
): AssemblyModelAsset | null {
  const localGeometryTemplate = cloneInlineAssemblyLocalGeometry(source)
  if (!localGeometryTemplate) {
    return null
  }
  const topologyPayload = createImportedAssemblyLocalTopologyPayload(localGeometryTemplate, unitContext)

  const inlineFileUuid = `inline-assembly:${assemblyFileUuid}:${nodeId}`
  const inlineFileId = `inline-assembly:${nodeId}`
  const manifest: AssemblyManifest = {
    assemblyFileId: inlineFileId,
    assemblyFileUuid: inlineFileUuid,
    version,
    rootNodeId: 'inline-root',
    nodesById: {
      'inline-root': {
        nodeId: 'inline-root',
        parentNodeId: null,
        name: nodeName,
        semanticKind: 'assembly',
        transform: [
          1, 0, 0, 0,
          0, 1, 0, 0,
          0, 0, 1, 0,
          0, 0, 0, 1,
        ],
      },
    },
    childIdsByNodeId: {
      'inline-root': [],
    },
    referencedPartUuids: [],
    unitContext,
  }

  return {
    fileUuid: inlineFileUuid,
    fileId: inlineFileId,
    version,
    cacheRevision: IMPORTED_MODEL_RUNTIME_CACHE_REVISION,
    partName: nodeName,
    sourceType: 'embedded',
    manifest,
    partAssetsByUuid: {},
    assemblyAssetsByUuid: {},
    warnings: [],
    bodyEntries: topologyPayload.bodyEntries,
    sceneTemplate: topologyPayload.sceneTemplate,
    localTopologySource: topologyPayload.localTopologySource,
  }
}

function collectNodeMaterialNames(asset?: PartAsset): string[] {
  if (!asset) {
    return []
  }
  const names = new Set<string>()
  Object.values(asset.meshCatalog).forEach((mesh) => {
    mesh.materialNames.forEach((materialName) => names.add(materialName))
  })
  return Array.from(names)
}

function buildNodeMeta(
  nodeId: string,
  manifest: AssemblyManifest,
  partAsset?: PartAsset,
  assemblyAsset?: AssemblyModelAsset,
): GlbHierarchyNodeMeta {
  const node = manifest.nodesById[nodeId]
  const isAssemblyNode = node.semanticKind === 'assembly'
  const materialNames = collectNodeMaterialNames(partAsset)
  // SolidWorks 语义下“实体”只在零件层展示，装配层不展示实体文件夹。
  const bodyEntries = isAssemblyNode
    ? []
    : (partAsset?.bodyEntries ?? assemblyAsset?.bodyEntries ?? [])
  const bodyCount = bodyEntries.length > 0
    ? bodyEntries.length
    : (partAsset ? Math.max(Object.keys(partAsset.meshCatalog).length, 1) : undefined)
  const hasExternalGeometry = Boolean(node.partFileUuid && (partAsset || assemblyAsset))
  return {
    semanticKind: node.semanticKind,
    assemblyNodeId: nodeId,
    hasGeometry: hasExternalGeometry,
    hasDirectGeometry: hasExternalGeometry,
    pmi: node.pmi,
    bodyCount,
    bodyEntries: bodyEntries.length > 0 ? bodyEntries : undefined,
    bodyPrimitiveIndicesById: !isAssemblyNode && partAsset
      ? partAsset.bodyPrimitiveIndicesById
      : undefined,
    materialNames,
    materialName: materialNames.length === 1 ? materialNames[0] : undefined,
    instanceIndex: node.instanceIndex,
    configurationName: node.configurationName,
    displayStateName: node.displayStateName,
    externalFileUuid: node.partFileUuid,
    externalAssemblyName: node.name,
  }
}

function createPartHierarchyFileInfo(partAsset: PartAsset): FileInfo {
  return {
    fileId: partAsset.fileId,
    size: BigInt(0),
    kind: '.glb',
    uuid: partAsset.fileUuid,
  } as FileInfo
}

function createAssemblyAssetFileInfo(asset: AssemblyModelAsset): FileInfo {
  return {
    fileId: asset.fileId,
    size: BigInt(0),
    kind: '.glb',
    uuid: asset.fileUuid,
  } as FileInfo
}

function rebaseReferencedPartHierarchyNode(
  node: GlbHierarchyNode,
  rootPath: string,
  parentObjectId: string,
  parentPath: string,
): GlbHierarchyNode {
  const relativePath = node.path.startsWith(`${rootPath}/`)
    ? node.path.slice(rootPath.length + 1)
    : node.name
  const path = parentPath ? `${parentPath}/${relativePath}` : relativePath
  return {
    ...node,
    parentObjectId,
    path,
    depth: path ? path.split('/').length - 1 : 0,
    children: node.children.map((child) =>
      rebaseReferencedPartHierarchyNode(child, rootPath, node.objectId, path)),
  }
}

function buildReferencedPartHierarchyChildren(
  hierarchy: GlbHierarchyNode | undefined,
  parentObjectId: string,
  parentPath: string,
): GlbHierarchyNode[] {
  if (!hierarchy) {
    return []
  }

  const normalizedRootName = normalizeImportedHierarchyDuplicateName(hierarchy.name)
  return hierarchy.children.flatMap((child) => {
    const normalizedChildName = normalizeImportedHierarchyDuplicateName(child.name)
    const shouldCollapseDuplicateAssemblyRoot =
      normalizedRootName.length > 0
      && normalizedChildName === normalizedRootName
      && !child.meta?.externalFileUuid
      && !child.meta?.hasDirectGeometry

    if (!shouldCollapseDuplicateAssemblyRoot) {
      return [rebaseReferencedPartHierarchyNode(child, hierarchy.path, parentObjectId, parentPath)]
    }

    if (child.children.length === 0) {
      return [rebaseReferencedPartHierarchyNode(child, hierarchy.path, parentObjectId, parentPath)]
    }

    return child.children.map((grandchild) =>
      rebaseReferencedPartHierarchyNode(grandchild, child.path, parentObjectId, parentPath))
  })
}

function shouldExposeReferencedPartHierarchy(
  node: AssemblyNode,
  partAsset?: PartAsset,
): boolean {
  if (!partAsset) {
    return true
  }
  if (!partAsset.fileUuid.startsWith('inline:')) {
    return true
  }
  // inline assembly 需要保留真实子节点；inline part 仅需折叠重复包装层。
  return node.semanticKind === 'assembly'
}

export function buildAssemblyHierarchy(
  manifest: AssemblyManifest,
  partAssetsByUuid: Record<string, PartAsset>,
  assemblyAssetsByUuid: Record<string, AssemblyModelAsset>,
  sceneBindings: SceneBindingIndex,
  referencedPartHierarchiesByAssemblyNodeId: Record<string, GlbHierarchyNode>,
): GlbHierarchyNode {
  const visit = (
    nodeId: string,
    parentObjectId: string | null,
    parentPath: string,
  ): GlbHierarchyNode => {
    const node = manifest.nodesById[nodeId]
    if (!node) {
      // nodeId 在 manifest 中不存在，跳过此节点，返回空占位节点
      return {
        objectId: nodeId,
        parentObjectId,
        path: parentPath ? `${parentPath}/__unknown` : '__unknown',
        name: '__unknown',
        nodeType: 'Group',
        selectable: false,
        depth: 0,
        meta: {
          semanticKind: 'unknown',
          hasGeometry: false,
          hasDirectGeometry: false,
          pmi: undefined,
          bodyCount: undefined,
          bodyEntries: undefined,
          materialNames: [],
          materialName: undefined,
          instanceIndex: undefined,
          configurationName: undefined,
          displayStateName: undefined,
          externalFileUuid: undefined,
          externalAssemblyName: undefined,
        },
        children: [],
      }
    }
    const selectionObjectId = sceneBindings.byAssemblyNodeId[nodeId]?.[0] ?? nodeId
    const partAsset = node.partFileUuid ? partAssetsByUuid[node.partFileUuid] : undefined
    const assemblyAsset = node.partFileUuid ? assemblyAssetsByUuid[node.partFileUuid] : undefined
    const path = parentPath ? `${parentPath}/${node.name}` : node.name
    const childIds = manifest.childIdsByNodeId[nodeId] ?? []
    const children = childIds
      .filter((childId) => !shouldSkipResolvedExternalReferenceFallback(
        node,
        manifest.nodesById[childId],
        partAsset,
        assemblyAsset,
      ))
      .map((childId) => visit(childId, selectionObjectId, path))
    const referencedPartChildren = shouldExposeReferencedPartHierarchy(node, partAsset)
      ? buildReferencedPartHierarchyChildren(
          referencedPartHierarchiesByAssemblyNodeId[nodeId],
          selectionObjectId,
          path,
        )
      : []
    const meta = buildNodeMeta(nodeId, manifest, partAsset, assemblyAsset)
    const selectionObject = sceneBindings.byObject3DUuid[selectionObjectId]
    if (partAsset && meta.bodyEntries && meta.bodyEntries.length > 0) {
      meta.bodyHighlightTargetsById = resolveBodyHighlightTargetsById(selectionObject, meta.bodyEntries)
    }
    const mergedChildren = [...children, ...referencedPartChildren]
    if (assemblyAsset && referencedPartChildren.length > 0) {
      meta.bodyEntries = undefined
      meta.bodyCount = undefined
      meta.materialNames = []
      meta.materialName = undefined
    }
    const hasChildGeometry = mergedChildren.some((child) => Boolean(child.meta?.hasGeometry))
    meta.hasGeometry = meta.hasGeometry || hasChildGeometry

    return {
      objectId: selectionObjectId,
      parentObjectId,
      path,
      name: node.name,
      nodeType: 'Group',
      selectable: Boolean(meta.hasGeometry || node.partFileUuid),
      depth: parentPath ? path.split('/').length - 1 : 0,
      meta,
      children: mergedChildren,
    }
  }

  return visit(manifest.rootNodeId, null, '')
}

function applyTransform(target: THREE.Object3D, elements: number[]): void {
  const matrix = new THREE.Matrix4().fromArray(elements)
  matrix.decompose(target.position, target.quaternion, target.scale)
}

function ensureBinding(
  sceneBindings: SceneBindingIndex,
  assemblyNodeId: string,
  object: THREE.Object3D,
): void {
  const list = sceneBindings.byAssemblyNodeId[assemblyNodeId] ?? []
  list.push(object.uuid)
  sceneBindings.byAssemblyNodeId[assemblyNodeId] = list
  sceneBindings.byObject3DUuid[object.uuid] = object
}

function bindPartInstanceMetadata(
  instanceRoot: THREE.Object3D,
  assemblyNodeId: string,
  partAsset: PartAsset,
  selectionIndex: SelectionIndex,
  sceneFileId: string,
  selectionProxyObjectId: string,
): void {
  instanceRoot.traverse((object) => {
    const localBinding = object.userData.partAssetLocal as { primitiveIds?: string[] } | undefined
    const selectionTarget =
      localBinding?.primitiveIds?.length
        ? {
            assemblyNodeId,
            partUuid: partAsset.fileUuid,
            primitiveId: localBinding.primitiveIds.length === 1 ? localBinding.primitiveIds[0] : undefined,
          }
        : {
            assemblyNodeId,
            partUuid: partAsset.fileUuid,
          }
    ImportedModelObjectMetadataService.writeMetadata(object, {
      fileId: sceneFileId,
      assemblyNodeId,
      partFileUuid: partAsset.fileUuid,
      selectionProxyObjectId,
      selectionTarget,
    })
    selectionIndex.byObject3DUuid[object.uuid] = selectionTarget
  })
}

function bindAssemblyInstanceMetadata(
  instanceRoot: THREE.Object3D,
  assemblyNodeId: string,
  assemblyAsset: AssemblyModelAsset,
  selectionIndex: SelectionIndex,
  sceneFileId: string,
  selectionProxyObjectId: string,
): void {
  instanceRoot.traverse((object) => {
    const selectionTarget = {
      assemblyNodeId,
      partUuid: assemblyAsset.fileUuid,
    }
    ImportedModelObjectMetadataService.writeMetadata(object, {
      fileId: sceneFileId,
      assemblyNodeId,
      partFileUuid: assemblyAsset.fileUuid,
      selectionProxyObjectId,
      selectionTarget,
    })
    selectionIndex.byObject3DUuid[object.uuid] = selectionTarget
  })
}

function collectReferencedPartHierarchy(
  instanceRoot: THREE.Object3D,
  partAsset: PartAsset,
): GlbHierarchyNode {
  const hierarchyRoot = resolveReferencedPartHierarchyRoot(instanceRoot)
  const strippedProxyValues = new Map<THREE.Object3D, string>()

  hierarchyRoot.traverse((object) => {
    const proxyObjectId = ImportedModelObjectMetadataService.takeSelectionProxyObjectId(object)
    if (proxyObjectId) {
      strippedProxyValues.set(object, proxyObjectId)
    }
  })

  try {
    return collectGlbHierarchy(
      hierarchyRoot,
      createPartHierarchyFileInfo(partAsset),
    ).hierarchy
  } finally {
    strippedProxyValues.forEach((proxyValue, object) => {
      ImportedModelObjectMetadataService.writeMetadata(object, { selectionProxyObjectId: proxyValue })
    })
  }
}

export function composeAssemblyModel(
  manifest: AssemblyManifest,
  partAssetsByUuid: Record<string, PartAsset>,
  assemblyAssetsByUuid: Record<string, AssemblyModelAsset>,
  file: FileInfo,
  warnings: string[] = [],
  sourceAssetKey?: string,
  resetRootNodeTransform = false,
  rootAssemblyAsset?: AssemblyModelAsset,
): ComposedAssemblyResult {
  const root = new THREE.Group()
  root.name = 'imported-assembly'
  const selectionIndex: SelectionIndex = { byObject3DUuid: {} }
  const sceneBindings: SceneBindingIndex = { byAssemblyNodeId: {}, byObject3DUuid: {} }
  const referencedPartHierarchiesByAssemblyNodeId: Record<string, GlbHierarchyNode> = {}

  const visit = (nodeId: string, parent: THREE.Object3D) => {
    const node = manifest.nodesById[nodeId]
    if (!node) {
      return
    }
    const instanceGroup = new THREE.Group()
    instanceGroup.name = node.name
    // 嵌套装配时，父 instanceGroup 已经持有当前引用节点的放置变换；
    // 子装配文件根节点若再应用一次自身根变换，会导致整棵子树整体偏移。
    const shouldResetCurrentNodeTransform = resetRootNodeTransform && nodeId === manifest.rootNodeId
    if (!shouldResetCurrentNodeTransform) {
      applyTransform(instanceGroup, node.transform)
    }
    ImportedModelObjectMetadataService.writeMetadata(instanceGroup, {
      fileId: file.fileId,
      assemblyNodeId: nodeId,
      partFileUuid: node.partFileUuid ?? '',
      selectionProxyObjectId: instanceGroup.uuid,
      selectionTarget: {
        assemblyNodeId: nodeId,
        partUuid: node.partFileUuid,
      },
    })
    addObject3DToParent(instanceGroup, parent)

    ensureBinding(sceneBindings, nodeId, instanceGroup)
    selectionIndex.byObject3DUuid[instanceGroup.uuid] = {
      assemblyNodeId: nodeId,
      partUuid: node.partFileUuid,
    }

    if (nodeId === manifest.rootNodeId && rootAssemblyAsset?.sceneTemplate) {
      const localSceneTemplate = cloneWithSharedGeometry(rootAssemblyAsset.sceneTemplate)
      if (resetRootNodeTransform) {
        localSceneTemplate.position.set(0, 0, 0)
        localSceneTemplate.quaternion.set(0, 0, 0, 1)
        localSceneTemplate.scale.set(1, 1, 1)
        localSceneTemplate.updateMatrix()
      }
      addObject3DToParent(localSceneTemplate, instanceGroup)
      if (rootAssemblyAsset.localTopologySource) {
        activateImportedModelTopologyHosts(instanceGroup, rootAssemblyAsset.localTopologySource)
      }
    }

    const partAsset = node.partFileUuid ? partAssetsByUuid[node.partFileUuid] : undefined
    const assemblyAsset = node.partFileUuid ? assemblyAssetsByUuid[node.partFileUuid] : undefined

    if (node.partFileUuid) {
      if (partAsset) {
        const instanceRoot = cloneWithSharedGeometry(partAsset.renderTemplate)
        instanceRoot.name = partAsset.partName
        bindPartInstanceMetadata(
          instanceRoot,
          nodeId,
          partAsset,
          selectionIndex,
          file.fileId,
          instanceGroup.uuid,
        )
        addObject3DToParent(instanceRoot, instanceGroup)
        referencedPartHierarchiesByAssemblyNodeId[nodeId] = collectReferencedPartHierarchy(
          instanceRoot,
          partAsset,
        )
        activateImportedModelTopologyHosts(instanceRoot, partAsset)
      } else {
        if (assemblyAsset) {
          const childResult = createCachedAssemblyResult(
            assemblyAsset,
            createAssemblyAssetFileInfo(assemblyAsset),
            true, // asNestedChild：告知 embedded 子装配重置 sceneTemplate 自身变换，父 instanceGroup 已持有正确变换
          )
          const childRoots = [...childResult.root.children]
          childRoots.forEach((childRoot) => {
            bindAssemblyInstanceMetadata(
              childRoot,
              nodeId,
              assemblyAsset,
              selectionIndex,
              file.fileId,
              instanceGroup.uuid,
            )
            addObject3DToParent(childRoot, instanceGroup)
          })
          instanceGroup.updateMatrixWorld(true)
          if (assemblyAsset.localTopologySource) {
            activateImportedModelTopologyHosts(instanceGroup, assemblyAsset.localTopologySource)
          }
          Object.assign(selectionIndex.byObject3DUuid, childResult.selectionIndex.byObject3DUuid)
          if (!assemblyAsset.fileUuid.startsWith('inline-assembly:')) {
            referencedPartHierarchiesByAssemblyNodeId[nodeId] = childResult.hierarchy
          }
        }
      }
    }

    const childIds = manifest.childIdsByNodeId[nodeId] ?? []
    childIds
      .filter((childId) => !shouldSkipResolvedExternalReferenceFallback(
        node,
        manifest.nodesById[childId],
        partAsset,
        assemblyAsset,
      ))
      .forEach((childId) => visit(childId, instanceGroup))
  }

  visit(manifest.rootNodeId, root)
  const hierarchy = buildAssemblyHierarchy(
    manifest,
    partAssetsByUuid,
    assemblyAssetsByUuid,
    sceneBindings,
    referencedPartHierarchiesByAssemblyNodeId,
  )

  const hierarchyByObjectId = new Map<string, typeof hierarchy>()
  const collectHierarchy = (node: typeof hierarchy) => {
    hierarchyByObjectId.set(node.objectId, node)
    node.children.forEach((child) => collectHierarchy(child))
  }
  collectHierarchy(hierarchy)

  root.traverse((object) => {
    const assemblyNodeId = ImportedModelObjectMetadataService.resolveAssemblyNodeId(object)
    if (!assemblyNodeId) {
      return
    }
    const hierarchyNode = hierarchyByObjectId.get(object.uuid)
    if (!hierarchyNode) {
      return
    }
    const hierarchyMetadata = {
      fileId: file.fileId,
      name: hierarchyNode.name,
      path: hierarchyNode.path,
      nodeType: object.type,
      selectable: hierarchyNode.selectable,
      parentObjectId: hierarchyNode.parentObjectId,
      depth: hierarchyNode.depth,
      meta: hierarchyNode.meta ? cloneGlbHierarchyUserDataMeta(hierarchyNode.meta) : undefined,
    }
    const selectionTarget = selectionIndex.byObject3DUuid[object.uuid]
      ?? ImportedModelObjectMetadataService.resolveSelectionTarget(object)
    ImportedModelObjectMetadataService.writeMetadata(object, {
      fileId: file.fileId,
      hierarchy: hierarchyMetadata,
      selectable: hierarchyNode.selectable,
      selectionTarget,
    })
    if (typeof hierarchyNode.meta?.isFixed === 'boolean') {
      AssemblyComponentFixationService.writeSceneObjectFixationState(
        object,
        hierarchyNode.meta.isFixed
          ? AssemblyComponentFixationState.Fixed
          : AssemblyComponentFixationState.Floating,
      )
    }
  })
  return {
    kind: 'composed-assembly',
    fileInfo: file,
    partName: extractPartName(file.fileId),
    root,
    hierarchy,
    partAssetsByUuid,
    assemblyAssetsByUuid,
    manifest,
    selectionIndex,
    sceneBindings,
    warnings,
    sourceAssetKey,
  }
}

function composeEmbeddedAssemblyModel(
  asset: AssemblyModelAsset,
  file: FileInfo,
  resetRootTransform = false,
  shouldActivateLocalTopology = true,
): ComposedAssemblyResult {
  const root = new THREE.Group()
  root.name = 'imported-assembly'

  const sceneTemplate = asset.sceneTemplate ? cloneWithSharedGeometry(asset.sceneTemplate) : new THREE.Group()
  if (!sceneTemplate.name.trim()) {
    sceneTemplate.name = asset.partName
  }

  // 当作为嵌套子装配被组合时（resetRootTransform=true），
  // 父层 instanceGroup 已通过 applyTransform(instanceGroup, node.transform) 还原了位置，
  // sceneTemplate 自身携带的 GLB 局部变换与 node.transform 描述同一件事，若不重置会双重叠加导致零件散落。
  if (resetRootTransform) {
    sceneTemplate.position.set(0, 0, 0)
    sceneTemplate.quaternion.set(0, 0, 0, 1)
    sceneTemplate.scale.set(1, 1, 1)
    sceneTemplate.updateMatrix()
  }

  addObject3DToParent(sceneTemplate, root)

  const { hierarchy } = collectGlbHierarchy(resolveReferencedPartHierarchyRoot(sceneTemplate), file)
  if (shouldActivateLocalTopology && asset.localTopologySource) {
    activateImportedModelTopologyHosts(root, asset.localTopologySource)
  }

  return {
    kind: 'composed-assembly',
    fileInfo: file,
    partName: asset.partName,
    root,
    hierarchy,
    partAssetsByUuid: {},
    assemblyAssetsByUuid: {},
    manifest: asset.manifest,
    selectionIndex: { byObject3DUuid: {} },
    sceneBindings: { byAssemblyNodeId: {}, byObject3DUuid: {} },
    warnings: [...asset.warnings],
    sourceAssetKey: buildAssemblyAssetKey(asset),
  }
}

export function createCachedAssemblyResult(
  asset: AssemblyModelAsset,
  file: FileInfo,
  asNestedChild = false,
): ComposedAssemblyResult {
  if (asset.sourceType === 'embedded') {
    // asNestedChild=true 时重置 sceneTemplate 变换，避免与父层 instanceGroup 的 node.transform 双重叠加
    return composeEmbeddedAssemblyModel(asset, file, asNestedChild, !asNestedChild)
  }

  return composeAssemblyModel(
    asset.manifest,
    asset.partAssetsByUuid,
    asset.assemblyAssetsByUuid,
    file,
    [...asset.warnings],
    buildAssemblyAssetKey(asset),
    asNestedChild,
    asset,
  )
}
